//src/components/Tasks.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, Timestamp, onSnapshot, arrayUnion, collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from './Header';
import Timeline from './TimeLine';
import Calendar from './Calendar';

// Khởi tạo Firebase Functions
const functions = getFunctions();

const MainPage = () => {
  // Lấy teamId từ tham số URL
  const { teamId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Trạng thái theo dõi tab đang hoạt động
  const [activeTab, setActiveTab] = useState('progress');
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' hoặc 'gantt'
  const [showSidebar, setShowSidebar] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  
  // Cài đặt mặc định cho công việc từ cài đặt người dùng
  const [userTaskDefaults, setUserTaskDefaults] = useState({
    defaultTaskPriority: 'medium',
    defaultTaskDuration: 1
  });
  
  const [newTask, setNewTask] = useState({
    text: '',
    description: '',
    start_date: Timestamp.fromDate(new Date()),
    duration: 1,
    type: 'task',
    priority: 'medium',
    progress: 0,
    assignees: []
  });
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedExecutors, setSelectedExecutors] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const sidebarRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Thêm trạng thái để lưu trữ tất cả phòng ban của người dùng
  const [userTeams, setUserTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [activeRoleTab, setActiveRoleTab] = useState('executor');

  // Lấy dữ liệu phòng ban
  const fetchTeamData = useCallback(async () => {
    if (!teamId) {
      console.log("Không có teamId được cung cấp");
      return;
    }
    
    try {
      console.log("Đang lấy dữ liệu phòng ban cho teamId:", teamId);
      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);
      
      if (teamSnap.exists()) {
        const data = teamSnap.data();
        console.log("Đã nhận dữ liệu phòng ban:", data);
        
        // Đảm bảo dữ liệu phòng ban có tên
        if (!data.name) {
          console.warn("Dữ liệu phòng ban thiếu thuộc tính name");
        }
        
        setTeamData({
          id: teamId,
          ...data
        });
        
        // Lấy thông tin thành viên phòng ban
        if (data.membersData && Array.isArray(data.membersData)) {
          setTeamMembers(data.membersData);
        } else {
          console.warn("Dữ liệu phòng ban thiếu mảng membersData");
          // Nếu không có dữ liệu thành viên ở định dạng đúng, khởi tạo mảng rỗng
          setTeamMembers([]);
        }
      } else {
        console.error('Không tìm thấy phòng ban');
        // Nếu phòng ban không tồn tại, quay lại trang danh sách phòng ban
        navigate('/Teams');
      }
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu phòng ban:', error);
    }
  }, [teamId, navigate]);
  
  // Thêm hàm để lấy tất cả phòng ban của người dùng
  const fetchUserTeams = async (userId) => {
    if (!userId) return;
    
    try {
      setLoadingTeams(true);
      
      // Nếu là system admin, load tất cả phòng ban
      const email = auth.currentUser?.email || '';
      const systemAdmins = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com'];
      if (systemAdmins.includes(email)) {
        const allSnap = await getDocs(collection(db, 'teams'));
        const allTeams = [];
        allSnap.forEach(doc => allTeams.push({ id: doc.id, ...doc.data() }));
        // Sắp xếp theo tên
        allTeams.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setUserTeams(allTeams);
        setLoadingTeams(false);
        return;
      }
      
      // Truy vấn tất cả phòng ban mà người dùng thuộc về
      const teamsQuery = query(
        collection(db, 'teams'),
        where('members', 'array-contains', userId)
      );
      
      const teamsSnapshot = await getDocs(teamsQuery);
      
      const teams = [];
      teamsSnapshot.forEach((doc) => {
        teams.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sắp xếp theo tên phòng ban
      teams.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
      });
      
      setUserTeams(teams);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách phòng ban của người dùng:', error);
    } finally {
      setLoadingTeams(false);
    }
  };
  
  // Xử lý chuyển đổi phòng ban
  const handleTeamChange = (newTeamId) => {
    if (newTeamId && newTeamId !== teamId) {
      navigate(`/team/${newTeamId}/tasks`);
    }
  };

  // Lấy dữ liệu người dùng từ Firestore
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Lấy tài liệu người dùng từ Firestore
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            // Kết hợp dữ liệu người dùng từ auth với dữ liệu từ Firestore
            const userData = {
              uid: user.uid,
              email: user.email,
              displayName: userSnap.data().displayName || user.displayName || 'Người dùng',
              photoURL: user.photoURL || 'https://via.placeholder.com/100',
              ...userSnap.data()
            };
            
            setUserData(userData);
            
            // Lấy cài đặt mặc định nếu có
            if (userData.taskDefaults) {
              console.log("Đã tải cài đặt mặc định của người dùng:", userData.taskDefaults);
              setUserTaskDefaults(userData.taskDefaults);
              
              // Đặt lại newTask với cài đặt mặc định của người dùng
              setNewTask(prev => ({
                ...prev,
                duration: userData.taskDefaults.defaultTaskDuration || 1,
                priority: userData.taskDefaults.defaultTaskPriority || 'medium'
              }));
            }
          } else {
            // Sử dụng dữ liệu auth nếu tài liệu Firestore không tồn tại
            setUserData({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || 'Người dùng',
              photoURL: user.photoURL || 'https://via.placeholder.com/100'
            });
          }
          
          // Lấy tất cả phòng ban của người dùng
          await fetchUserTeams(user.uid);
          
        } catch (error) {
          console.error("Lỗi khi lấy dữ liệu người dùng:", error);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // useEffect riêng cho việc lấy dữ liệu phòng ban
  useEffect(() => {
    if (teamId) {
      console.log("Đang lấy dữ liệu phòng ban do thay đổi teamId:", teamId);
      fetchTeamData();
    }
  }, [teamId, navigate, fetchTeamData]);

  // Kiểm tra quyền hạn phòng ban
  useEffect(() => {
    const checkTeamPermissions = async () => {
      if (!teamId || !auth.currentUser) return;
      
      try {
        // Kiểm tra nếu người dùng là quản trị viên phòng ban
        const checkIsAdminFunction = httpsCallable(functions, 'checkIsTeamAdmin');
        const adminResult = await checkIsAdminFunction({ teamId });
        setIsAdmin(adminResult.data.isAdmin);
        
        // Kiểm tra nếu người dùng là quản lý phòng ban
        const checkIsManagerFunction = httpsCallable(functions, 'checkIsTeamManager');
        const managerResult = await checkIsManagerFunction({ teamId });
        setIsManager(managerResult.data.isManager);
        
        console.log('Quyền hạn phòng ban:', { isAdmin: adminResult.data.isAdmin, isManager: managerResult.data.isManager });
      } catch (error) {
        console.error('Lỗi khi kiểm tra quyền hạn phòng ban:', error);
      }
    };
    
    checkTeamPermissions();
  }, [teamId]);

  // Xử lý click bên ngoài sidebar để đóng nó
  const handleOutsideClick = (e) => {
    if (sidebarRef.current && !sidebarRef.current.contains(e.target) && 
        e.target.classList.contains('sidebar-overlay')) {
      setShowSidebar(false);
    }
  };

  // Thêm trình nghe phím ESC để đóng sidebar
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && showSidebar) {
        setShowSidebar(false);
      }
    };

    if (showSidebar) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showSidebar]);

  // Xử lý mở hộp thoại tạo công việc
  const handleOpenCreateTaskModal = () => {
    // Áp dụng cài đặt công việc mặc định của người dùng khi mở hộp thoại
    setNewTask({
      text: '',
      description: '',
      start_date: Timestamp.fromDate(new Date()),
      duration: userTaskDefaults.defaultTaskDuration || 1,
      type: 'task',
      priority: userTaskDefaults.defaultTaskPriority || 'medium',
      progress: 0,
      assignees: []
    });
    setSelectedExecutors([]);
    setSelectedCollaborators([]);
    setSelectedSupervisors([]);
    setSelectedApprovers([]);
    setShowCreateTaskModal(true);
  };

  // Xử lý tạo công việc
  const handleCreateTask = async (e) => {
    e.preventDefault();

    // Xác thực biểu mẫu
    if (!newTask.text.trim()) {
      alert('Tên công việc là bắt buộc');
      return;
    }

    // Format ngày bắt đầu
    let formattedStartDate = newTask.start_date;
    if (typeof newTask.start_date === 'string') {
      formattedStartDate = new Date(newTask.start_date).toISOString();
    } else if (formattedStartDate instanceof Timestamp) {
      formattedStartDate = formattedStartDate.toDate().toISOString();
    }

    // Nếu không có executors được chọn, thêm currentUser làm executor mặc định
    const executorUsers = selectedExecutors.length > 0
      ? selectedExecutors
      : [{ uid: userData.uid, displayName: userData.displayName, email: userData.email }];

    const executors = executorUsers.map(user => ({
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      role: 'executor'
    }));
    
    const collaborators = selectedCollaborators.map(user => ({
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      role: 'collaborator'
    }));
    
    const supervisors = selectedSupervisors.map(user => ({
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      role: 'supervisor'
    }));
    
    const approvers = selectedApprovers.map(user => ({
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      role: 'approver'
    }));
    
    // Kết hợp tất cả người tham gia
    const allParticipants = [...executors, ...collaborators, ...supervisors, ...approvers];

    // Assignees chỉ giữ duy nhất theo UID để tránh key trùng và thông báo trùng
    const uniqueAssignees = allParticipants.reduce((acc, participant) => {
      if (!participant?.uid) {
        return acc;
      }

      if (!acc.some(item => item.uid === participant.uid)) {
        acc.push(participant);
      }
      return acc;
    }, []);
    
    // Tạo dữ liệu công việc
    const taskData = {
      text: newTask.text,
      description: newTask.description,
      start_date: formattedStartDate,
      duration: parseInt(newTask.duration) || 1,
      type: newTask.type,
      priority: newTask.priority,
      progress: newTask.progress,
      status: 'notStarted',
      participants: allParticipants,
      // assignees dùng danh sách duy nhất theo uid
      assignees: uniqueAssignees
    };
    
    try {
      // Sử dụng Cloud Function để tạo công việc
      const createTaskFunction = httpsCallable(functions, 'createTask');
      const result = await createTaskFunction({
        teamId: teamId,
        taskData: taskData
      });
      
      console.log('Tạo công việc thành công:', result.data);
    } catch (error) {
      console.error('Lỗi createTask:', error);
      const backendMessage = error?.message || 'Không thể tạo công việc. Vui lòng thử lại.';
      alert(backendMessage);
      return;
    }
    
    // Đặt lại biểu mẫu, sử dụng cài đặt mặc định của người dùng
    setNewTask({
      text: '',
      description: '',
      start_date: Timestamp.fromDate(new Date()),
      duration: userTaskDefaults.defaultTaskDuration || 1,
      type: 'task',
      priority: userTaskDefaults.defaultTaskPriority || 'medium',
      progress: 0,
      assignees: []
    });
    
    // Đặt lại danh sách người được chọn
    setSelectedExecutors([]);
    setSelectedCollaborators([]);
    setSelectedSupervisors([]);
    setSelectedApprovers([]);
    
    setShowCreateTaskModal(false);
    
    // Tăng khóa làm mới để kích hoạt làm mới giao diện
    setRefreshKey(prevKey => prevKey + 1);
    
    // Làm mới dữ liệu phòng ban để cập nhật danh sách công việc
    fetchTeamData();
  };
  
  // Xử lý thêm/xóa người phụ trách công việc
  const toggleAssignee = (member, role) => {
    const updateSelected = (selectedList, setSelectedList) => {
      if (selectedList.some(a => a.uid === member.uid)) {
        setSelectedList(selectedList.filter(a => a.uid !== member.uid));
      } else {
        setSelectedList([...selectedList, member]);
      }
    };

    switch (role) {
      case 'executor':
        updateSelected(selectedExecutors, setSelectedExecutors);
        break;
      case 'collaborator':
        updateSelected(selectedCollaborators, setSelectedCollaborators);
        break;
      case 'supervisor':
        updateSelected(selectedSupervisors, setSelectedSupervisors);
        break;
      case 'approver':
        updateSelected(selectedApprovers, setSelectedApprovers);
        break;
      default:
        break;
    }
  };
  
  // Chuyển đổi chế độ xem
  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };
  
  // Hiển thị thành phần dựa trên tab đang hoạt động và chế độ xem
  const renderComponent = () => {
    if (!teamId) {
      console.log("renderComponent được gọi không có teamId");
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Chưa chọn phòng ban. Vui lòng chọn một phòng ban để xem công việc.</p>
        </div>
      );
    }
    
    console.log("Đang hiển thị thành phần cho teamId:", teamId, "activeTab:", activeTab, "viewMode:", viewMode);
    
    try {
      switch (activeTab) {
        case 'progress':
          return viewMode === 'timeline' 
            ? <Timeline key={`timeline-${teamId}`} teamId={teamId} refreshKey={refreshKey} />
            : <Calendar key={`gantt-${teamId}`} teamId={teamId} refreshKey={refreshKey} />;
        default:
          return viewMode === 'timeline'
            ? <Timeline key={`timeline-${teamId}`} teamId={teamId} refreshKey={refreshKey} />
            : <Calendar key={`gantt-${teamId}`} teamId={teamId} refreshKey={refreshKey} />;
      }
    } catch (error) {
      console.error("Lỗi khi hiển thị thành phần:", error);
      return (
        <div className="flex items-center justify-center h-64 bg-red-50">
          <div className="text-center p-6">
            <p className="text-red-500 font-medium mb-2">Lỗi khi tải thành phần</p>
            <p className="text-gray-600 text-sm">{error.message}</p>
          </div>
        </div>
      );
    }
  };

  // Hiển thị trang chính
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <div className="pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <div className="flex-1 min-w-0">
                {/* Thêm bộ chọn phòng ban */}
                <div className="flex items-center space-x-3">
                  <select
                    value={teamId || ''}
                    onChange={(e) => handleTeamChange(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-emerald-300 focus:ring focus:ring-emerald-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-2"
                    disabled={loadingTeams}
                  >
                    {loadingTeams ? (
                      <option>Đang tải phòng ban...</option>
                    ) : userTeams.length === 0 ? (
                      <option>Không có phòng ban nào</option>
                    ) : (
                      userTeams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))
                    )}
                  </select>
                  
                  <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate mb-1">
                    Công việc
                  </h2>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                  {teamData && (
                    <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <i className="fas fa-users mr-1.5 text-gray-400 dark:text-gray-500"></i>
                      {teamMembers.length} thành viên
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 md:mt-0 flex">
                <button
                  type="button"
                  onClick={handleOpenCreateTaskModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Thêm công việc
                </button>
              </div>
            </div>
            
            {/* Tab chuyển đổi chế độ xem */}
            <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
              <div className="flex justify-between items-center">
                <div className="flex">
                  <button
                    onClick={() => toggleViewMode('timeline')}
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      viewMode === 'timeline'
                        ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                    }`}
                  >
                    <i className="fas fa-stream mr-2"></i>
                    Dòng thời gian
                  </button>
                  <button
                    onClick={() => toggleViewMode('gantt')}
                    className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                      viewMode === 'gantt'
                        ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                    }`}
                  >
                    <i className="fas fa-bars-progress mr-2"></i>
                    Lịch
                  </button>
                </div>
              </div>
            </div>
            
            {/* Khu vực nội dung chính */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              {renderComponent()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Hộp thoại tạo công việc */}
      {showCreateTaskModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleCreateTask}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                        Tạo công việc mới
                      </h3>
                      <div className="mt-4 space-y-4">
                        <div>
                          <label htmlFor="task-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Tên công việc
                          </label>
                          <input
                            type="text"
                            name="task-name"
                            id="task-name"
                            value={newTask.text}
                            onChange={(e) => setNewTask({...newTask, text: e.target.value})}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Nhập tên công việc"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="task-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Mô tả
                          </label>
                          <textarea
                            id="task-description"
                            name="task-description"
                            rows="3"
                            value={newTask.description}
                            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="Mô tả công việc"
                          ></textarea>
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Ngày bắt đầu
                            </label>
                            <input
                              type="date"
                              name="start-date"
                              id="start-date"
                              value={newTask.start_date instanceof Timestamp 
                                ? new Date(newTask.start_date.seconds * 1000).toISOString().split('T')[0]
                                : typeof newTask.start_date === 'string' 
                                  ? newTask.start_date
                                  : new Date().toISOString().split('T')[0]
                              }
                              onChange={(e) => setNewTask({...newTask, start_date: e.target.value})}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Thời hạn (ngày)
                              {newTask.duration === userTaskDefaults.defaultTaskDuration && (
                                <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">(mặc định)</span>
                              )}
                            </label>
                            <input
                              type="number"
                              name="duration"
                              id="duration"
                              min="1"
                              value={newTask.duration}
                              onChange={(e) => setNewTask({...newTask, duration: e.target.value})}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Mức ưu tiên
                              {newTask.priority === userTaskDefaults.defaultTaskPriority && (
                                <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">(mặc định)</span>
                              )}
                            </label>
                            <select
                              id="priority"
                              name="priority"
                              value={newTask.priority}
                              onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            >
                              <option value="low">Thấp</option>
                              <option value="medium">Trung bình</option>
                              <option value="high">Cao</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="task-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Loại
                            </label>
                            <select
                              id="task-type"
                              name="task-type"
                              value={newTask.type}
                              onChange={(e) => setNewTask({...newTask, type: e.target.value})}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            >
                              <option value="task">Công việc</option>
                              <option value="milestone">Mốc quan trọng</option>
                              <option value="project">Dự án</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Người tham gia
                          </label>
                          
                          {/* Tab chọn loại vai trò */}
                          <div className="mb-2 border-b border-gray-200 dark:border-gray-700">
                            <nav className="flex -mb-px">
                              <button
                                type="button"
                                onClick={() => setActiveRoleTab('executor')}
                                className={`px-4 py-2 text-sm font-medium ${
                                  activeRoleTab === 'executor'
                                    ? 'border-b-2 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                              >
                                <i className="fas fa-user-cog mr-1"></i>
                                Người thực hiện {selectedExecutors.length > 0 && `(${selectedExecutors.length})`}
                              </button>
                              <button
                                type="button"
                                onClick={() => setActiveRoleTab('collaborator')}
                                className={`px-4 py-2 text-sm font-medium ${
                                  activeRoleTab === 'collaborator'
                                    ? 'border-b-2 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                              >
                                <i className="fas fa-people-arrows mr-1"></i>
                                Người phối hợp {selectedCollaborators.length > 0 && `(${selectedCollaborators.length})`}
                              </button>
                              <button
                                type="button"
                                onClick={() => setActiveRoleTab('supervisor')}
                                className={`px-4 py-2 text-sm font-medium ${
                                  activeRoleTab === 'supervisor'
                                    ? 'border-b-2 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                              >
                                <i className="fas fa-user-shield mr-1"></i>
                                Người giám sát {selectedSupervisors.length > 0 && `(${selectedSupervisors.length})`}
                              </button>
                              <button
                                type="button"
                                onClick={() => setActiveRoleTab('approver')}
                                className={`px-4 py-2 text-sm font-medium ${
                                  activeRoleTab === 'approver'
                                    ? 'border-b-2 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                              >
                                <i className="fas fa-user-check mr-1"></i>
                                Người phê duyệt {selectedApprovers.length > 0 && `(${selectedApprovers.length})`}
                              </button>
                            </nav>
                          </div>
                          
                          <div className="mt-2 border border-gray-300 dark:border-gray-600 rounded-md p-2 max-h-40 overflow-y-auto">
                            {teamMembers.length > 0 ? (
                              <div>
                                {activeRoleTab === 'executor' && (
                                  <div className="space-y-1">
                                    <div className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-sm">
                                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Người thực hiện: Người chịu trách nhiệm chính thực hiện công việc</p>
                                    </div>
                                    {teamMembers.map((member) => (
                                      <div key={`executor-${member.uid}`} className="flex items-center p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-sm">
                                        <input
                                          type="checkbox"
                                          id={`executor-${member.uid}`}
                                          checked={selectedExecutors.some(a => a.uid === member.uid)}
                                          onChange={() => toggleAssignee(member, 'executor')}
                                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded dark:bg-gray-700"
                                        />
                                        <label
                                          htmlFor={`executor-${member.uid}`}
                                          className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                                        >
                                          {member.displayName || member.email}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {activeRoleTab === 'collaborator' && (
                                  <div className="space-y-1">
                                    <div className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-sm">
                                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Người phối hợp: Người phối hợp, hỗ trợ trong quá trình thực hiện</p>
                                    </div>
                                    {teamMembers.map((member) => (
                                      <div key={`collaborator-${member.uid}`} className="flex items-center p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-sm">
                                        <input
                                          type="checkbox"
                                          id={`collaborator-${member.uid}`}
                                          checked={selectedCollaborators.some(a => a.uid === member.uid)}
                                          onChange={() => toggleAssignee(member, 'collaborator')}
                                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded dark:bg-gray-700"
                                        />
                                        <label
                                          htmlFor={`collaborator-${member.uid}`}
                                          className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                                        >
                                          {member.displayName || member.email}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {activeRoleTab === 'supervisor' && (
                                  <div className="space-y-1">
                                    <div className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-sm">
                                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Người giám sát: Người theo dõi, giám sát tiến độ công việc</p>
                                    </div>
                                    {teamMembers.map((member) => (
                                      <div key={`supervisor-${member.uid}`} className="flex items-center p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-sm">
                                        <input
                                          type="checkbox"
                                          id={`supervisor-${member.uid}`}
                                          checked={selectedSupervisors.some(a => a.uid === member.uid)}
                                          onChange={() => toggleAssignee(member, 'supervisor')}
                                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded dark:bg-gray-700"
                                        />
                                        <label
                                          htmlFor={`supervisor-${member.uid}`}
                                          className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                                        >
                                          {member.displayName || member.email}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {activeRoleTab === 'approver' && (
                                  <div className="space-y-1">
                                    <div className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-sm">
                                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Người phê duyệt: Người có quyền phê duyệt, chấp nhận kết quả công việc</p>
                                    </div>
                                    {teamMembers.map((member) => (
                                      <div key={`approver-${member.uid}`} className="flex items-center p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-sm">
                                        <input
                                          type="checkbox"
                                          id={`approver-${member.uid}`}
                                          checked={selectedApprovers.some(a => a.uid === member.uid)}
                                          onChange={() => toggleAssignee(member, 'approver')}
                                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded dark:bg-gray-700"
                                        />
                                        <label
                                          htmlFor={`approver-${member.uid}`}
                                          className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                                        >
                                          {member.displayName || member.email}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-500 dark:text-gray-400 text-sm">Không tìm thấy thành viên phòng ban</p>
                            )}
                          </div>
                          
                          <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <span className={`inline-block w-2 h-2 rounded-full mr-1 ${selectedExecutors.length > 0 ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                              <span>Người thực hiện: {selectedExecutors.length}</span>
                            </div>
                            <div className="flex items-center">
                              <span className={`inline-block w-2 h-2 rounded-full mr-1 ${selectedCollaborators.length > 0 ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                              <span>Người phối hợp: {selectedCollaborators.length}</span>
                            </div>
                            <div className="flex items-center">
                              <span className={`inline-block w-2 h-2 rounded-full mr-1 ${selectedSupervisors.length > 0 ? 'bg-yellow-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                              <span>Người giám sát: {selectedSupervisors.length}</span>
                            </div>
                            <div className="flex items-center">
                              <span className={`inline-block w-2 h-2 rounded-full mr-1 ${selectedApprovers.length > 0 ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                              <span>Người phê duyệt: {selectedApprovers.length}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Tạo
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateTaskModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    Hủy
                  </button>
                </div>
                <div className="px-4 py-2 text-center text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                  Bạn có thể thay đổi cài đặt công việc mặc định trong trang <a href="/settings" className="text-emerald-600 dark:text-emerald-400 hover:underline">Cài đặt</a> của bạn.
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPage;